import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';

// ============================================
// Interfaces
// ============================================

export type AlertChannelType = 'email' | 'slack' | 'telegram';

export interface EmailChannelConfig {
  recipients: string[];
}

export interface SlackChannelConfig {
  webhook_url: string;
}

export interface TelegramChannelConfig {
  bot_token: string;
  chat_id: string;
}

export type AlertChannelConfig = EmailChannelConfig | SlackChannelConfig | TelegramChannelConfig;

export interface AlertChannel {
  id: string;
  workspace_id: string;
  name: string;
  type: AlertChannelType;
  config: AlertChannelConfig;
  enabled: boolean;
  status: 'active' | 'archived';
  created_at: string;
  updated_at: string;
}

export interface CreateAlertChannelData {
  name: string;
  type: AlertChannelType;
  config: AlertChannelConfig;
  enabled?: boolean;
}

export type UpdateAlertChannelData = Partial<CreateAlertChannelData>;

export interface AlertChannelTestResult {
  ok: boolean;
  error?: string;
}

// ============================================
// Service
// ============================================

export const alertChannelsService = {
  query: async (filters: { limit?: number; offset?: number; search?: string; where?: WhereCondition } = {}): Promise<{ data: AlertChannel[]; total: number }> => {
    const searchWhere: WhereCondition | undefined = filters.search
      ? { alert_channel: { name: { op: 'ilike', value: `%${filters.search}%` } } } : undefined;
    // Both may be present — a search and a status filter have to hold together.
    const where: WhereCondition | undefined = searchWhere && filters.where
      ? { $and: [searchWhere, filters.where] }
      : (filters.where ?? searchWhere);
    const payload: QueryPayload = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 100,
      order_by: 'alert_channel.created_at',
      order: 'desc',
      ...(where && { where }),
    };
    const response = await api.post<QueryResponse<AlertChannel>>('/alert-channels/query', payload);
    return { data: response.data.data, total: response.data._meta?.total ?? 0 };
  },

  getById: async (id: string): Promise<AlertChannel> => {
    const response = await api.get<ApiResponse<AlertChannel>>(`/alert-channels/${id}`);
    return response.data.data!;
  },

  create: async (data: CreateAlertChannelData): Promise<AlertChannel> => {
    const response = await api.post<ApiResponse<AlertChannel>>('/alert-channels', data);
    return response.data.data!;
  },

  update: async (id: string, data: UpdateAlertChannelData): Promise<AlertChannel> => {
    const response = await api.patch<ApiResponse<AlertChannel>>(`/alert-channels/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/alert-channels/${id}`);
  },

  unarchive: async (id: string): Promise<AlertChannel> => {
    const response = await api.patch<ApiResponse<AlertChannel>>(`/alert-channels/${id}/unarchive`);
    return response.data.data!;
  },

  test: async (id: string): Promise<AlertChannelTestResult> => {
    const response = await api.post<ApiResponse<AlertChannelTestResult>>(`/alert-channels/${id}/test`);
    return response.data.data!;
  },
};
